import React, { useEffect, useState } from "react";
import { IconButton, TableCell, TableRow } from "@mui/material";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import { getTranslateByKey } from "@/main/services/utils";
import MySuppliersRegister from "./mySuppliersRegister";
import * as S from "./styles";
import * as StylesShared from "@/main/components/shared/styles";
import KOGPaginationGrid from "../paginationGrid/paginationGrid";
import { supplierApi } from "@/main/api";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { SupplierCompanyRelationFilterRequest } from "@/main/models/request/supplierCompanyRelationRequest";
import { SupplierCompanyRelationResponse } from "@/main/models/response/supplierCompanyRelationResponse";
import { SaveSupplierCompanyRelationRequest } from "@/main/models/request";
import MySuppliersEdit from "./mySuppliersEdit";
import { isNil } from "lodash";
import TooltipComponent from "../tooltip/tooltipComponent";
import Info from "@mui/icons-material/Info";
import KogInputTextFilter from "../inputText/inputTextFilter";

interface Props {
	distributionRegionReducer?: any;
	supplierTypeReducer?: any;
	manufacturerReducer?: any;
	watcherCountry?: any;
	guidBodyshop?: any;
	guidInsurer?: any;
	formFilters: any;
	setFormFilters: React.Dispatch<any>;
	blacklistAndMySuppliersDeleteItem: SaveSupplierCompanyRelationRequest[];
	setBlacklistAndMySuppliersDeleteItem: (cb: (data: any[]) => any[]) => void;
	isInsurerView?: boolean;
}

function MySuppliersInfo({
	distributionRegionReducer,
	supplierTypeReducer,
	manufacturerReducer,
	watcherCountry,
	guidBodyshop,
	guidInsurer,
	formFilters,
	setFormFilters,
	blacklistAndMySuppliersDeleteItem,
	setBlacklistAndMySuppliersDeleteItem,
	isInsurerView = false,
}: Props) {
	const ID_SCROLLABLE_TARGET = "id-scrollable-target-supplierRelations-favorite";

	const baseFilter: SupplierCompanyRelationFilterRequest = {
		guidBodyshop: guidBodyshop,
		guidInsurer: guidInsurer,
		restricted: false,
		pageIndex: PaginationEnum.PageIndexIntial,
		pageSize: PaginationEnum.PageSize10,
	};

	const [open, setOpen] = useState<boolean>(false);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [dataGrid, setDataGrid] = useState<SupplierCompanyRelationResponse[]>([]);
	const [openEdit, setOpenEdit] = useState<boolean>(false);
	const [supplierSelected, setSupplierSelected] = useState<SupplierCompanyRelationResponse | null>(null);

	const rowHeaders = [
		getTranslateByKey("information.rfc"),
		getTranslateByKey("information.supplierCompany"),
		getTranslateByKey("information.userDateTime"),
		isInsurerView ? (
			<StylesShared.RowHeaderTextCenter>
				{getTranslateByKey("information.trustedSupplier")}
				<TooltipComponent title={getTranslateByKey("information.tooltipDescriptionTrustedSupplier")} placement="top">
					<span>
						<IconButton size="medium" color="primary" style={{ marginLeft: 5 }}>
							<Info />
						</IconButton>
					</span>
				</TooltipComponent>
			</StylesShared.RowHeaderTextCenter>
		) : null,
		isInsurerView ? (
			<StylesShared.RowHeaderTextCenter>
				{getTranslateByKey("information.refundLimit")}
			</StylesShared.RowHeaderTextCenter>
		) : null,
		getTranslateByKey("information.actions"),
	].filter((x) => !isNil(x));

	function closeModal() {
		setOpen(false);
	}

	function resetGrid() {
		setFormFilters(null);
	}

	async function fetchData(request: any) {
		return supplierApi.getSupplierRestrictedAndFavorite(request).then((response) => response.data);
	}

	function handleDeleteMySupplierInfo(guid: string) {
		setBlacklistAndMySuppliersDeleteItem((prev) => [...prev, { guid: guid }]);
		//forceUpdate();
	}

	function handleChangeFilter(newFilter: string) {
		if (newFilter === formFilters.searchTerm) return;
		const request: SupplierCompanyRelationFilterRequest = {
			...baseFilter,
			searchTerm: newFilter,
		};

		setDataGrid([]);
		setFormFilters(request);
	}

	function closeEditModal() {
		setOpenEdit(false);
		setSupplierSelected(null);
	}

	function handleOpenEdit(supplierRelation: SupplierCompanyRelationResponse) {
		setSupplierSelected(supplierRelation);
		setOpenEdit(true);
	}

	useEffect(() => {
		resetGrid();
	}, []);

	useEffect(() => {
		if (formFilters == null) {
			setDataGrid([]);
			setFormFilters(baseFilter);
		}
	}, [formFilters]);

	return (
		<>
			<S.Container>
				<S.ContainerTitle>
					<S.HeaderText>{getTranslateByKey("information.mySuppliersInfo")}</S.HeaderText>
					<SecondaryButton
						style={{
							padding: ".2rem .8rem",
							marginTop: ".6rem",
						}}
						onClick={() => setOpen(true)}
						id="id-register-button-addMySuppliers"
					>
						{getTranslateByKey("information.addMySuppliers")}
					</SecondaryButton>

					<S.AreaFilterGrid>
						<KogInputTextFilter
							id="id-filter-mysuppliers-search"
							label={getTranslateByKey("information.typeToFilter")}
							onDebounce={handleChangeFilter}
						/>
					</S.AreaFilterGrid>
				</S.ContainerTitle>

				<S.ContainerScroll id={ID_SCROLLABLE_TARGET}>
					<KOGPaginationGrid
						idScrollableTarget={ID_SCROLLABLE_TARGET}
						showTitleResults={false}
						totalRecords={totalRecords}
						setTotalRecords={setTotalRecords}
						dataGrid={dataGrid}
						formFilters={formFilters}
						setDataGrid={setDataGrid}
						fetchMore={fetchData}
					>
						<BoxGrid rowHeaders={rowHeaders}>
							{dataGrid.map((supplierRelation) => (
								<TableRow key={supplierRelation.guidSupplier}>
									<TableCell width="10%">{supplierRelation.registrationNumber}</TableCell>
									<TableCell width="15%">{supplierRelation.companyName}</TableCell>
									<TableCell width="25%">{supplierRelation.userDateTime}</TableCell>
									{isInsurerView && (
										<>
											<TableCell width="25%" align="center">
												{supplierRelation.isTrusted
													? getTranslateByKey("information.yes")
													: getTranslateByKey("information.no")}
											</TableCell>
											<TableCell width="25%" align="center">
												{!isNil(supplierRelation.refundLimitFormated)
													? supplierRelation.refundLimitFormated
													: getTranslateByKey("information.limitNotDefined")}
											</TableCell>
										</>
									)}
									<TableCell width="1%" align="center">
										{!blacklistAndMySuppliersDeleteItem.some((item) => item.guid == supplierRelation.guid) && (
											<>
												{isInsurerView && (
													<S.IconEdit
														id={`id-mysuppliers-edit-${supplierRelation.registrationNumber}`}
														onClick={() => handleOpenEdit(supplierRelation)}
													/>
												)}

												<S.IconClose
													id={`id-mysuppliers-delete-${supplierRelation.registrationNumber}`}
													onClick={() => handleDeleteMySupplierInfo(supplierRelation.guid)}
												/>
											</>
										)}
									</TableCell>
								</TableRow>
							))}
						</BoxGrid>
					</KOGPaginationGrid>
				</S.ContainerScroll>
			</S.Container>
			<MySuppliersRegister
				open={open}
				closeModal={closeModal}
				distributionRegionReducer={distributionRegionReducer}
				supplierTypeReducer={supplierTypeReducer}
				manufacturerReducer={manufacturerReducer}
				watcherCountry={watcherCountry}
				guidBodyshop={guidBodyshop}
				guidInsure={guidInsurer}
				resetGrid={resetGrid}
				isInsurerView={isInsurerView}
			/>

			{!isNil(supplierSelected) && openEdit && (
				<MySuppliersEdit
					open={openEdit}
					closeModal={closeEditModal}
					supplier={supplierSelected}
					resetGrid={resetGrid}
				/>
			)}
		</>
	);
}

export default MySuppliersInfo;
